import { CrossCircledIcon } from "@radix-ui/react-icons";
import React, { Component } from "react";
import { FormattedMessage, type IntlShape, injectIntl } from "react-intl";
import { type ConnectedProps, connect } from "react-redux";
import { Link, type RouteComponentProps } from "react-router-dom";

import { eraseStateProp } from "~/actions/dashboard";
import { getPatientIdWithError } from "~/actions/get_patient_id";
import { getToken } from "~/actions/get_token";
import { isAdditionalAlignersCourse, isPlanApprovalStatus, Status } from "~/common/courses";
import { featureFlags } from "~/common/feature-flags";
import { remoteLog } from "~/common/logging";
import { getLastCorrection, isPatient } from "~/common/patient";
import { isUser } from "~/common/user";
import { Button } from "~/components/ui/button";
import { Layout } from "~/components/ui/layout";
import { Loader } from "~/components/ui/loader";
import { Portlet } from "~/components/ui/portlet";
import { ScrollToTop } from "~/components/ui/scroll-to-top";
import { PatientViewer } from "~/features/doctor-viewer/patient-viewer";
import { setDocumentTitle } from "~/hooks/use-document-title";
import { clearExtraServices, getExtraServices } from "~/slices/services";
import { selectUser } from "~/slices/user";
import type { RootState } from "~/store";

import PatientAskQuestion from "./patient_ask_question";
import Patient3DPlan from "./patient_show/patient_3d_plan";
import { PatientAdditionalAlignersCard } from "./patient_show/patient_additional_aligners";
import { PatientAdditionalServices } from "./patient_show/patient_additional_services";
import { PatientComments } from "./patient_show/patient_comments";
import PatientDeliveryBlock from "./patient_show/patient_delivery";
import { PatientId } from "./patient_show/patient_id";
import { PatientInvoice } from "./patient_show/patient_invoice";
import { PatientPayment } from "./patient_show/patient_payment";
import { PatientPlanExpectedDate } from "./patient_show/patient_plan_expected_date";
import { canShowPowerCapCard, PatientPowerCapCard } from "./patient_show/patient_power_cap";
import { PatientStatus } from "./patient_show/patient_status";
import { PatientCard } from "./patient_show/patient-card";

const mapStateToProps = (state: RootState) => {
  return {
    user: selectUser(state),
    patient: state.patient,
    token: state.token,
  };
};

const mapDispatchToProps = {
  getPatient: getPatientIdWithError,
  getToken,
  clearExtraServices,
  getExtraServices,
  eraseStateProp,
};

type PatientPageProps = PropsFromRedux & { intl: IntlShape } & RouteComponentProps<{
    patient_id: string;
  }>;

type PatientPageState = {
  isNotFound: boolean;
};

class PatientPage extends Component<PatientPageProps, PatientPageState> {
  constructor(props: PatientPageProps) {
    super(props);
    this.state = {
      isNotFound: false,
    };
  }

  async componentDidMount() {
    window.scrollTo(0, 0);

    const { patient_id } = this.props.match.params;
    this.props.clearExtraServices();

    void this.props.getToken();
    void this.props.getExtraServices(Number(patient_id));

    try {
      await this.props.getPatient(Number(patient_id), true);
    } catch (err) {
      if (err instanceof Error && err.cause instanceof Response && err.cause.status == 404) {
        this.setState({ isNotFound: true });
      }
    }

    setDocumentTitle(this.props.intl.formatMessage({ id: "pat.block.right.header" }), patient_id);
  }

  componentDidCatch(e: Error) {
    remoteLog(e, "patient_page_body");
  }

  componentWillUnmount() {
    this.props.eraseStateProp("patient");
  }

  renderQuestionSection() {
    if (isPlanApprovalStatus(this.props.patient.status)) {
      return (
        <div className="portlet light bordered" id="question-side-block" style={{ textAlign: "center" }}>
          <PatientAskQuestion />
        </div>
      );
    }
    return null;
  }

  render() {
    if (this.state.isNotFound) {
      return (
        <Layout>
          <Portlet as="main" className="tw-p-5">
            <div data-testid="patient-not-found" className="tw-flex tw-items-center tw-gap-2 max-desktop:tw-justify-center">
              <CrossCircledIcon className="tw-size-5 tw-text-red-600" />
              <span className="tw-font-semibold">
                <FormattedMessage id="pat.block.not_found" />
              </span>
            </div>
          </Portlet>
        </Layout>
      );
    }

    if (!isPatient(this.props.patient) || !isUser(this.props.user)) {
      return (
        <Layout>
          <Loader />
        </Layout>
      );
    }

    const { patient, user, token } = this.props;
    const lastCorrection = getLastCorrection(patient);
    const { can_order_correction, can_end_treatment, can_order_retainers, can_order_additional_aligners } = lastCorrection.order_options;
    const can_set_bonus_patient = patient.bonus_set_free;

    return (
      <Layout>
        <div className="row">
          <div className="col-md-8">
            <PatientViewer patient={patient} />
            {token && typeof patient.media["3d-plan"] == "object" && patient.media["3d-plan"].response ? (
              <Patient3DPlan patient={patient} token={token} />
            ) : (
              <Portlet>
                <div className="portlet-body">
                  {!featureFlags.alignv ? <PatientPlanExpectedDate date={lastCorrection.plan_date} /> : null}
                  <Button asChild>
                    <Link to={`/pages/patient/${patient.patient_id}/report`}>
                      <FormattedMessage id="pat.plan.report" />
                    </Link>
                  </Button>
                </div>
              </Portlet>
            )}
            {can_order_correction || can_end_treatment || can_order_retainers || can_set_bonus_patient ? <PatientAdditionalServices patient={patient} /> : null}
            {patient.status != Status.UNFILLED_CASE ? <PatientInvoice patient={patient} /> : null}
            <PatientComments patient={patient} user={user} />
            <PatientCard patient={patient} user={user} />
          </div>
          <div className="col-md-4">
            <PatientId patient={patient} />
            <PatientStatus patient={patient} />
            <PatientPayment patient={patient} />
            {isAdditionalAlignersCourse(patient.course.course_id) ? null : <PatientDeliveryBlock />}
            {this.renderQuestionSection()}
            {can_order_additional_aligners || isAdditionalAlignersCourse(patient.course.course_id) || patient.additional_aligners_patients.length > 0 ? (
              <PatientAdditionalAlignersCard patient={patient} />
            ) : null}
            {canShowPowerCapCard(patient) ? <PatientPowerCapCard patient={patient} /> : null}
          </div>
        </div>

        <ScrollToTop />
      </Layout>
    );
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps);
type PropsFromRedux = ConnectedProps<typeof connector>;
export default connector(injectIntl(PatientPage));
